import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../data/database';
import { CheckIcon } from './Icons';
import centralUpdateStatus, { detectUnitType } from '../utils/updateUnitStatus';

function ContainerLoading({ container, user, onUpdate }) {
  const [loadingUnit, setLoadingUnit] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [condNote, setCondNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
  const customers = DB.get('customers') || [];
  const unitIds = container.units || [];
  const units = unitIds.map(uid => allUnits.find(u => u.id === uid)).filter(Boolean);

  // Get or initialize unitsData
  const unitsData = container.unitsData || [];
  const getLoadData = (unitId) => unitsData.find(u => u.unit_id === unitId);
  const loadedCount = units.filter(u => getLoadData(u.id)?.loaded_at).length;
  const allLoaded = units.length > 0 && loadedCount === units.length;
  const isLoading = container.status === 'loading';

  const getName = (u) => u.make ? `${u.make} ${u.model}` : u.engineName || u.partName || 'Unknown';
  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || '';

  const saveContainer = (updated) => {
    const containers = DB.get('containers') || [];
    DB.set('containers', containers.map(c => c.id === container.id ? updated : c));
    onUpdate(updated);
  };

  const startLoading = () => {
    // Initialize unitsData for all units if not done yet
    const initData = units.map(u => {
      const existing = unitsData.find(d => d.unit_id === u.id);
      if (existing) return existing;
      return { unit_id: u.id, unit_sku: u.sku || '', unit_description: getName(u) };
    });
    saveContainer({
      ...container,
      status: 'loading',
      loadingStartedAt: new Date().toISOString(),
      unitsData: initData,
    });
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newPhotos = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      try {
        const compressed = await compressImage(file);
        const url = await uploadImage(compressed);
        newPhotos.push(url);
      } catch (err) {
        console.warn('Photo upload failed:', err);
      }
    }
    setPhotos(prev => [...prev, ...newPhotos]);
    setUploading(false);
  };

  const compressImage = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  const submitLoaded = (unitId) => {
    if (photos.length < 2) { alert('Please upload at least 2 photos of this unit.'); return; }

    const currentData = Array.isArray(container.unitsData) && container.unitsData.length > 0 ? container.unitsData : units.map(u => ({ unit_id: u.id, unit_sku: u.sku || '', unit_description: getName(u) }));
    let found = false;
    const updatedData = currentData.map(ud => {
      if (ud.unit_id === unitId) {
        found = true;
        return {
          ...ud,
          loaded_at: new Date().toISOString(),
          loaded_by: user.id,
          loading_photos: photos,
          loading_condition: condNote || 'good',
          loading_notes: condNote,
        };
      }
      return ud;
    });

    if (!found) {
      const unit = allUnits.find(u => u.id === unitId);
      updatedData.push({
        unit_id: unitId, unit_sku: unit?.sku || '', unit_description: getName(unit || {}),
        loaded_at: new Date().toISOString(), loaded_by: user.id,
        loading_photos: photos, loading_condition: condNote || 'good', loading_notes: condNote,
      });
    }

    saveContainer({ ...container, unitsData: updatedData });

    const unit = allUnits.find(u => u.id === unitId);
    if (unit && unit.status !== 'loading') {
      centralUpdateStatus(unitId, detectUnitType(unit), 'loading', { containerId: container.id, updatedBy: user.id });
    }

    setLoadingUnit(null);
    setPhotos([]);
    setCondNote('');
  };

  if (!['planning', 'booked', 'loading'].includes(container.status)) return null;
  if (units.length === 0) return null;

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--blue)' }}>
          Loading
        </div>
        {isLoading && (
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>
            <span style={{ color: 'var(--green)', fontWeight: 700 }}>{loadedCount}</span> of {units.length} loaded
          </div>
        )}
      </div>

      {/* Progress bar */}
      {isLoading && (
        <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3, marginBottom: 14, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${(loadedCount / units.length) * 100}%`, background: allLoaded ? 'var(--green)' : 'var(--blue)', transition: 'width 0.3s', borderRadius: 3 }} />
        </div>
      )}

      {/* Start Loading button */}
      {!isLoading && (
        <button className="btn btn-primary" onClick={startLoading}>
          Start Loading ({units.length} units)
        </button>
      )}

      {/* Unit cards */}
      {isLoading && units.map(unit => {
        const ld = getLoadData(unit.id);
        const isLoaded = !!ld?.loaded_at;
        const isActive = loadingUnit === unit.id;

        return (
          <div key={unit.id} style={{
            padding: 12, background: isLoaded ? 'rgba(63,185,80,0.06)' : 'var(--bg3)',
            border: `1px solid ${isLoaded ? 'rgba(63,185,80,0.3)' : isActive ? 'var(--blue)' : 'var(--border)'}`,
            borderRadius: 10, marginBottom: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 48, height: 36, borderRadius: 6, overflow: 'hidden', background: 'var(--bg)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {unit.images?.length
                  ? <img src={unit.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 10, color: 'var(--text3)' }}>—</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getName(unit)}</div>
                <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{unit.sku}</div>
                {unit.assignedTo && <div style={{ fontSize: 10, color: 'var(--yellow)' }}>→ {getCustomerName(unit.assignedTo)}</div>}
              </div>

              {isLoaded ? (
                <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 700, flexShrink: 0 }}>✅ Loaded</span>
              ) : (
                <button className="btn btn-secondary btn-sm" onClick={() => { setLoadingUnit(isActive ? null : unit.id); setPhotos([]); setCondNote(''); }}
                  style={{ flexShrink: 0 }}>
                  {isActive ? 'Cancel' : 'Mark Loaded'}
                </button>
              )}
            </div>

            {/* Show loading photos with delete */}
            {isLoaded && ld.loading_photos?.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {ld.loading_photos.map((url, i) => (
                  <div key={i} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => window.open(url, '_blank')}>
                    <img src={url} alt="" style={{ width: 56, height: 42, borderRadius: 4, objectFit: 'cover' }} />
                    <button type="button" onClick={e => {
                      e.stopPropagation();
                      if (!window.confirm('Remove this image?')) return;
                      const updatedData = (container.unitsData || []).map(ud =>
                        ud.unit_id === unit.id ? { ...ud, loading_photos: (ud.loading_photos || []).filter((_, idx) => idx !== i) } : ud
                      );
                      saveContainer({ ...container, unitsData: updatedData });
                    }} style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: 'var(--red)', border: 'none', color: '#fff', fontSize: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                ))}
                {ld.loading_notes && <div style={{ fontSize: 10, color: 'var(--text3)', width: '100%' }}>{ld.loading_notes}</div>}
              </div>
            )}

            {/* Loading form */}
            {isActive && !isLoaded && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <div style={{ marginBottom: 10 }}>
                  <label className="form-label">Photos <span className="req">*</span> (minimum 2)</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                    {photos.map((url, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={url} alt="" style={{ width: 64, height: 48, borderRadius: 6, objectFit: 'cover' }} />
                        <button type="button" onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                          style={{ position: 'absolute', top: -4, right: -4, width: 18, height: 18, borderRadius: '50%', background: 'var(--red)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                      </div>
                    ))}
                  </div>

                  {/* Upload buttons — gallery + camera */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>
                      📷 Take Photo / Choose File
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" multiple capture="environment"
                      style={{ display: 'none' }} onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
                  </div>

                  {uploading && <div style={{ fontSize: 11, color: 'var(--blue)', marginTop: 6 }}>Uploading...</div>}
                  {!uploading && photos.length < 2 && <div style={{ fontSize: 10, color: 'var(--yellow)', marginTop: 6 }}>Upload at least 2 photos</div>}
                </div>

                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="form-label">Condition Note (optional)</label>
                  <input className="form-input" value={condNote} onChange={e => setCondNote(e.target.value)}
                    placeholder="e.g. Minor scratch on bumper" style={{ fontSize: 12 }} />
                </div>

                <button className="btn btn-success btn-sm" disabled={photos.length < 2 || uploading}
                  onClick={() => submitLoaded(unit.id)}>
                  <CheckIcon />Confirm Loaded ({photos.length} photos)
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ContainerLoading;
